import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpHeaderConstants } from '../http-header.constant';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { SpinnerService } from '../spinner/spinner.service';
import { AppInstallSearch } from '../../models/appcloud/app-install-search.model';
import { AppInstall } from '../../models/appcloud/app-install.model';

@Injectable({
    providedIn: 'root',
})
export class AppInstallService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private APP_INSTALLS_URL: string = environment.publicApiHost + '/appcloud/app-installs';
    private SERVICE_NAME = 'AppInstallService';
    private GET_BY_RESPONSYS_INSTALL_UUID_METHOD_NAME: string = this.SERVICE_NAME + '.getByResponsysInstallDetailUuid';
    private CREATE_METHOD_NAME: string = this.SERVICE_NAME + '.create';
    private UPDATE_METHOD_NAME: string = this.SERVICE_NAME + '.update';
    private GET_METHOD_NAME: string = this.SERVICE_NAME + '.get';
    private FIND_ALL_METHOD_NAME: string = this.SERVICE_NAME + '.findAll';

    findAll(search: AppInstallSearch, additionalHeaders: any = {}): Observable<AppInstall[]> {
        this.#spinnerService.addProcess(this.FIND_ALL_METHOD_NAME, 'Loading app install details...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
            params: new HttpParams(),
        };

        for (const [key, value] of Object.entries(search)) {
            if (value) {
                httpOptions.params = httpOptions.params.set(key, value);
            }
        }

        return this.#http.get<AppInstall[]>(this.APP_INSTALLS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving app install data from Relationship One. Please try again later.'
                );
                return of<AppInstall[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(this.FIND_ALL_METHOD_NAME))
        );
    }

    getByResponsysInstallDetailUuid(
        responsysInstallDetailUuid: string,
        additionalHeaders: any = {}
    ): Observable<AppInstall[]> {
        this.#spinnerService.addProcess(
            this.GET_BY_RESPONSYS_INSTALL_UUID_METHOD_NAME + '.' + responsysInstallDetailUuid,
            'Loading app install details...'
        );
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
            params: new HttpParams().set('responsysInstallDetailUuid', responsysInstallDetailUuid),
        };

        return this.#http.get<AppInstall[]>(this.APP_INSTALLS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving app install data from Relationship One. Please try again later.'
                );
                return of<AppInstall[]>();
            }),
            finalize(() =>
                this.#spinnerService.completeProcess(
                    this.GET_BY_RESPONSYS_INSTALL_UUID_METHOD_NAME + '.' + responsysInstallDetailUuid
                )
            )
        );
    }

    get(id: number, additionalHeaders: any = {}): Observable<AppInstall> {
        this.#spinnerService.addProcess(this.GET_METHOD_NAME, 'Loading app install details...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
        };

        return this.#http.get<AppInstall>(this.APP_INSTALLS_URL + '/' + id, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving app install data from Relationship One. Please try again later.'
                );
                return of<AppInstall>();
            }),
            finalize(() => this.#spinnerService.completeProcess(this.GET_METHOD_NAME))
        );
    }

    create(appInstall: AppInstall, additionalHeaders: any = {}): Observable<AppInstall> {
        this.#spinnerService.addProcess(this.CREATE_METHOD_NAME, 'Creating new configuration...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
        };

        return this.#http.post<AppInstall>(this.APP_INSTALLS_URL, appInstall, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while sending data to Relationship One. Please try again later.'
                );
                return of<AppInstall>();
            }),
            finalize(() => this.#spinnerService.completeProcess(this.CREATE_METHOD_NAME))
        );
    }

    update(id: number, appInstall: AppInstall, additionalHeaders: any = {}): Observable<AppInstall> {
        this.#spinnerService.addProcess(this.UPDATE_METHOD_NAME + '.' + id, 'Updating your configuration...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http.patch<AppInstall>(this.APP_INSTALLS_URL + '/' + id, appInstall, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while sending data to Relationship One. Please try again later.'
                );
                return of<AppInstall>();
            }),
            finalize(() => this.#spinnerService.completeProcess(this.UPDATE_METHOD_NAME + '.' + id))
        );
    }
}
